/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CONFIGURATION CENTRALE DES PAGES - VERSION 5.3 (FULLY DYNAMIC) 
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * RÈGLE D'OR:
 *   - 1 seule source de vérité = PAGES_CONFIG_.sections
 *   - Si un facet/section est ajouté à UNE page → le DataLoader charge les données
 *   - Si data est vide → la section NE S'AFFICHE PAS
 *   - Si data a des données → la section S'AFFICHE
 *
 * NOUVEAUTÉ v5.3 — paginationProps ciblé par section:
 *   Les adapterProps des sections *Grid wrappent loadMore/loadMore0 avec
 *   le contexte { sectionId, facetKey, dataKey } injecté par SectionRenderer.
 *   → reloadFacets(['bestSellersGrid']) recharge UNIQUEMENT bestSellers.
 *   → Aucune autre section n'est affectée.
 */

import {
    FaCog, FaPalette, FaDesktop, FaMobileAlt, FaSave, FaUndo,
    FaHome, FaShoppingCart, FaUser, FaBox, FaStore, FaEye,
    FaEdit, FaTags, FaLayerGroup, FaCheck, FaTimes, FaMagic,
    FaSpinner, FaChevronDown, FaChevronUp, FaInfoCircle, FaPlay,
    FaHeading, FaFont, FaAlignLeft, FaAlignCenter, FaAlignRight,
    FaList, FaArrowUp, FaArrowDown, FaEyeSlash, FaTrash, FaPlus,
    FaSortNumericDown, FaQuestionCircle, FaTablet, FaSync, FaLink,
    FaStar, FaFire, FaTag, FaSearch,
} from 'react-icons/fa';
import {
    getElakayLanguageContext,
    laguangeElakay,
} from '../dataElakay';
import { PAGE_FACETS } from './allFacets';
import { formatLakayShopTitle, separateLakayWord } from "../../../utils/lakayShopName";

const __elakayText = (key, fallback, values = null) => {
    const infoSource = getElakayLanguageContext();
    const text = infoSource?.languageAPI
        ? laguangeElakay(
            'frontElakayHardcodedText',
            infoSource.languageAPI
        )?.pagesConfig?.[key] || fallback
        : fallback;

    if (!values) return text;
    return text.replace(/\{([A-Za-z0-9_]+)\}/g, (match, name) =>
        Object.hasOwn(values, name) ? String(values[name] ?? '') : match
    );
};

const buildPageSections = (pageId, sectionOptions = {}) => {
    const facets = PAGE_FACETS[pageId] || [];

    return facets.map((sectionId, order) => ({
        sectionId,
        order,
        ...(sectionOptions[sectionId] || {}),
    }));
};

// ─────────────────────────────────────────────────────────────────────────────
// LOADER TYPES
// ─────────────────────────────────────────────────────────────────────────────

export const LOADER_TYPES = {
    PRODUCTS:        'products',
    LAKAY_USER:      'lakayUser',
    SHOP_USER:       'shopUser',
    USER_VIEWED:     'userViewed',
    ORDERS:          'orders',
    CATEGORIES:      'categories',
    PRODUCT_PREVIEW: 'productPreview',
    COMMENTS:        'comments',
    SEARCH:          'search',
    INVESTMENTS:     'investments',
    JOBS:            'jobs',
    CART:            'cart',
    NONE:            'none',
};

export const PRODUCTS_BASE_FACET = '__PRODUCTS_BASE__';

// ═══════════════════════════════════════════════════════════════════════════
// HELPER INTERNE — buildGridAdapterProps
// ═══════════════════════════════════════════════════════════════════════════
//
// Factorise l'adapterProps commun à TOUTES les sections *Grid.
//
// CLEF v5.3:
//   paginationProps contient { sectionId, facetKey, dataKey } injectés par
//   SectionRenderer. On wrap loadMore/loadMore0 pour transmettre ce contexte
//   aux callbacks définis dans buildSectionPaginationProps (DynamicPageRenderer).
//   → Chaque clic "load more" recharge UNIQUEMENT le facet de CETTE section.
//
// Params:
//   data           — données de la section (products, bestSellers, etc.)
//   sectionProps   — props de la section (skip, limit, promoMaker, ...)
//   context        — { paginationProps, laguangeElakay, ... }
//   defaultLimit   — limit par défaut si absent (ex: 12 pour bestSellersGrid)

const buildGridAdapterProps = ({ data, sectionProps, context, defaultLimit = 12 }) => {
    const { getComponent, ASSETS } = require('./ComponentRegistry');
    const Card             = getComponent('Card');
    const ButtonPagination = getComponent('ButtonPagination');
    const { paginationProps, laguangeElakay } = context;

    // ── Contexte section injecté par SectionRenderer ──────────────────
    // { sectionId, facetKey, dataKey } sont disponibles dans paginationProps
    // car SectionRenderer les y a ajoutés (voir DynamicPageRenderer.jsx)
    const sectionCtx = {
        sectionId: paginationProps?.sectionId,
        facetKey:  paginationProps?.facetKey,
        dataKey:   paginationProps?.dataKey,
    };

    return {
        productsByArrival: data,
        skip:        paginationProps?.skip        ?? sectionProps.skip  ?? 0,
        limit:       paginationProps?.limit       ?? sectionProps.limit ?? defaultLimit,
        size:        paginationProps?.size        ?? 0,
        siseProduct: paginationProps?.siseProduct ?? 0,

        // ✅ Wrappers ciblés: transmettent { sectionId, facetKey } au callback
        // → reloadFacets([sectionId]) dans buildSectionPaginationProps
        // → UNIQUEMENT ce facet est rechargé, pas les autres sections
        loadMore:  () => paginationProps?.loadMore?.(sectionCtx),
        loadMore0: () => paginationProps?.loadMore0?.(sectionCtx),

        prodCat:      sectionProps.prodCat      || '',
        laguangeElakay,
        Card,
        ButtonPagination,
        promoMaker:   sectionProps.promoMaker   || false,
        btnShopPromo: sectionProps.btnShopPromo || (() => {}),
        refShopName:  sectionProps.refShopName  || '',
        elakayEmptyBox: ASSETS.elakayEmptyBox,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// SECTIONS LIBRARY
// ─────────────────────────────────────────────────────────────────────────────

export const SECTIONS_LIBRARY = {

    // ═══════════════════════════════════════════════════════════════════════
    // SLIDERS
    // ═══════════════════════════════════════════════════════════════════════

    elakayCategories: {
        id: 'elakayCategories',
        component: 'Slider',
        facetKey: 'elakayCategories',
        dataKey: 'elakayCategories',
        loaderType: LOADER_TYPES.CATEGORIES,
        defaultProps: {
            circle: true,
            wm0: 3,
            classSlider: 'container',
            sliderShow: true,
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            ProductSwiper: data,
            User: context.infoSource?.readUser,
            myStyles: context.homeStyles,
        }),
        meta: { get title() { return __elakayText("t001", "Elakay Categories"); }, color: '#4A90D9' },
    },

    categoryItems: {
        id: 'categoryItems',
        component: 'SliderWithLoading',
        facetKey: 'categoryItems',
        dataKey: 'categoryItems',
        loaderType: LOADER_TYPES.CATEGORIES,
        defaultProps: {
            circle: true,
            wm0: 3,
            classSlider: 'container',
            sliderShow: true,
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            homeStyles: context.homeStyles,
        }),
        meta: { get title() { return __elakayText("t002", "Categories"); }, color: '#4A90D9' },
    },

    newProducts: {
        id: 'newProducts',
        component: 'Slider',
        facetKey: 'newProducts',
        dataKey: 'productsByArrival',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            nameSliderKey: 'newProduct',
            linkSlider: '/new-product',
        },
        adapterProps: ({ data, sectionProps, context }) => {
            const { infoSource, homeStyles, categoryContext } = context;
            let nameSlider = sectionProps.nameSlider;
            if (sectionProps.nameSliderKey) {
                const baseTitle = infoSource.laguangeElakay?.('homePage', infoSource.languageAPI)?.[sectionProps.nameSliderKey] || '';
                nameSlider = categoryContext?.categoryName
                    ? `${categoryContext.categoryName} ${baseTitle}`
                    : baseTitle;
            }
            let linkSlider = sectionProps.linkSlider;
            if (sectionProps.dynamicLink && categoryContext?.categoryId) {
                linkSlider = `${sectionProps.linkSlider}?id=${categoryContext.categoryId}&cat=${categoryContext.categoryName}`;
            }
            return {
                ProductSwiper: data,
                User: infoSource?.readUser,
                nameSlider,
                linkSlider,
                myStyles: homeStyles,
                laguangeElakay: context.laguangeElakay,
            };
        },
        meta: { get title() { return __elakayText("t003", "New Arrivals"); }, color: '#FF69B4' },
    },

    bestSellers: {
        id: 'bestSellers',
        component: 'Slider',
        facetKey: 'bestSellers',
        dataKey: 'productsBySell',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            nameSliderKey: 'bestSellers',
            linkSlider: '/best-sellers',
            dynamicLink: true,
        },
        adapterProps: ({ data, sectionProps, context }) => {
            const { infoSource, homeStyles, categoryContext } = context;
            let nameSlider = sectionProps.nameSlider;
            if (sectionProps.nameSliderKey) {
                const baseTitle = infoSource.laguangeElakay?.('homePage', infoSource.languageAPI)?.[sectionProps.nameSliderKey] || '';
                nameSlider = categoryContext?.categoryName
                    ? `${categoryContext.categoryName} ${baseTitle}`
                    : baseTitle;
            }
            let linkSlider = sectionProps.linkSlider;
            if (sectionProps.dynamicLink && categoryContext?.categoryId) {
                linkSlider = `${sectionProps.linkSlider}?id=${categoryContext.categoryId}&cat=${categoryContext.categoryName}`;
            }
            return {
                ProductSwiper: data,
                User: infoSource?.readUser,
                nameSlider,
                linkSlider,
                myStyles: homeStyles,
                laguangeElakay: context.laguangeElakay,
            };
        },
        meta: { get title() { return __elakayText("t004", "Best Sellers"); }, color: '#FFD700' },
    },

    dealTodays: {
        id: 'dealTodays',
        component: 'Slider',
        facetKey: 'dealTodays',
        dataKey: 'dealToDayProducts',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            nameSliderKey: 'dealDay',
            linkSlider: '/deal-today',
            dynamicLink: true,
        },
        adapterProps: ({ data, sectionProps, context }) => {
            const { infoSource, homeStyles, categoryContext } = context;
            let nameSlider = sectionProps.nameSlider;
            if (sectionProps.nameSliderKey) {
                const baseTitle = infoSource.laguangeElakay?.('homePage', infoSource.languageAPI)?.[sectionProps.nameSliderKey] || '';
                nameSlider = categoryContext?.categoryName
                    ? `${categoryContext.categoryName} ${baseTitle}`
                    : baseTitle;
            }
            let linkSlider = sectionProps.linkSlider;
            if (sectionProps.dynamicLink && categoryContext?.categoryId) {
                linkSlider = `${sectionProps.linkSlider}?id=${categoryContext.categoryId}&cat=${categoryContext.categoryName}`;
            }
            return {
                ProductSwiper: data,
                User: infoSource?.readUser,
                nameSlider,
                linkSlider,
                myStyles: homeStyles,
                laguangeElakay: context.laguangeElakay,
            };
        },
        meta: { get title() { return __elakayText("t005", "Today Deals"); }, color: '#DC143C' },
    },

    userView: {
        id: 'userView',
        component: 'Slider',
        facetKey: 'userView',
        dataKey: 'userViewedProduct',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            nameSliderKey: 'yourViewed',
            linkSlider: '/user-viewed',
            CataID: true,
        },
        adapterProps: ({ data, sectionProps, context }) => {
            const { infoSource, homeStyles } = context;
            let nameSlider = sectionProps.nameSlider;
            if (sectionProps.nameSliderKey) {
                nameSlider = infoSource.laguangeElakay?.('homePage', infoSource.languageAPI)?.[sectionProps.nameSliderKey] || '';
            }
            return {
                ProductSwiper: data,
                User: infoSource?.readUser,
                nameSlider,
                linkSlider: sectionProps.linkSlider,
                myStyles: homeStyles,
                laguangeElakay: context.laguangeElakay,
            };
        },
        meta: { get title() { return __elakayText("t006", "Your Views"); }, color: '#20B2AA' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // MATRICES SWIPER
    // ═══════════════════════════════════════════════════════════════════════

    historySearchMatrix: {
        id: 'historySearchMatrix',
        component: 'SwiperfollowShopUserMatrix',
        facetKey: 'historySearchMatrix',
        dataKey: 'historySearchMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            slideLinkText: 'viewAll',
            facet: 'historySearchMatrix',
            inView: true,
            titleKey: 't007',
            cateryNameStyle: true,
            maxProducts: 4,
            content_BackGroundColor: '195, 100%, 30%',
            // Section-level "See More" (new /search-history all-cards page).
            // The per-card "View All" (slideLinkText, inside each card) is
            // untouched — it keeps opening /search-history/:categoryId.
            linkSlider: '/search-history',
        },
        adapterProps: ({ data }) => ({
            productsrMatrix: data,
        }),
        meta: { get title() { return __elakayText("t008", "Search History"); }, color: '#FFA500' },
    },

    userViewMatrix: {
        id: 'userViewMatrix',
        component: 'SwiperfollowShopUserMatrix',
        facetKey: 'userViewMatrix',
        dataKey: 'userViewMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            slideLinkText: 'viewAll',
            facet: 'userViewMatrix',
            inView: true,
            titleKey: 't009',
            maxProducts: 4,
            cateryNameStyle: true,
            content_BackGroundColor: '195, 100%, 30%',
            // Section-level "See More" (new /viewing-activity all-cards page).
            // The per-card "View All" (slideLinkText, inside each card) is
            // untouched — it keeps opening /viewing-activity/:categoryId.
            linkSlider: '/viewing-activity',
        },
        adapterProps: ({ data }) => ({
            productsrMatrix: data,
        }),
        meta: { get title() { return __elakayText("t010", "Views Matrix"); }, color: '#FF6B6B' },
    },

    followShopUser: {
        id: 'followShopUser',
        component: 'SwiperfollowShopUserMatrix',
        facetKey: 'followShopUser',
        dataKey: 'followShopUserMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {
            facet: 'followShopUser',
            inView: true,
            titleKey: 't011',
            maxProducts: 4,
            cateryNameStyle: true,
            content_BackGroundColor: '195, 100%, 30%',
            // Section-level "See More" (new /shops-you-follow all-cards page).
            // Each card's own link (to /lakay-ShopSeller/:owner) is untouched.
            linkSlider: '/shops-you-follow',
        },
        // adapterProps: ({ data }) => ({
        //     productsrMatrix: data,
        // }),
                 adapterProps: ({ data, sectionProps, context }) => {
        const { reloadFacets } = context;

        // ✅ Retourne la Promise de reloadFacets
        // → permet au composant appelant de .then() après le chargement
        const loadProductsByArrival = (_filters, sectionIds) => {
            if (!reloadFacets) return Promise.resolve();
            const ids = Array.isArray(sectionIds)
                ? sectionIds
                : sectionIds
                    ? [sectionIds]
                    : ['followMe', 'followShopUser'];
            return reloadFacets(ids); // ← return la Promise
        };

        return {
             productsrMatrix: data,
            loadProductsByArrival,
        };
    },
        meta: { get title() { return __elakayText("t012", "Followed Shops"); }, color: '#7B68EE' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // "ALL CARDS" GRID SECTIONS — section-level "See More" destinations.
    // selfLoading: true — MatrixCardsGrid fetches its own paginated page of
    // groups directly (facet + paginateGroups:true), same as ProductGridPage
    // does for userViewGrid/bestSellersGrid/etc. Home's own historySearchMatrix/
    // userViewMatrix/followShopUser sections above never send paginateGroups,
    // so their unsliced request (and the Home slider) is completely
    // unaffected — this is a separate, opt-in request shape on the SAME
    // facet, not a new endpoint. Same FollowPageMatrice card (via
    // MatrixCardsGrid — see swiperUserProd/MatrixCardsGrid.jsx), just a
    // responsive wrap/grid + Load More instead of the Home Swiper. Each
    // card's OWN link (slideLinkText / shop link) is untouched.
    // ═══════════════════════════════════════════════════════════════════════

    historySearchMatrixGrid: {
        id: 'historySearchMatrixGrid',
        component: 'MatrixCardsGrid',
        facetKey: 'historySearchMatrix',
        dataKey: 'historySearchMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG
        requiresPagination: true,
        defaultProps: {
            slideLinkText: 'viewAll',
            facet: 'historySearchMatrix',
            // Response key from /productsF for this facet — here it happens
            // to equal `facet`, but MatrixCardsGrid always reads THIS prop
            // (never assumes facet === response key, see followShopUserGrid
            // below where they differ: facet 'followShopUser' → response key
            // 'followShopUserMatrix').
            dataKey: 'historySearchMatrix',
            cateryNameStyle: true,
            maxProducts: 4,
            content_BackGroundColor: '195, 100%, 30%',
        },
        meta: { get title() { return __elakayText("t008", "Search History"); }, color: '#FFA500' },
    },

    userViewMatrixGrid: {
        id: 'userViewMatrixGrid',
        component: 'MatrixCardsGrid',
        facetKey: 'userViewMatrix',
        dataKey: 'userViewMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG
        requiresPagination: true,
        defaultProps: {
            slideLinkText: 'viewAll',
            facet: 'userViewMatrix',
            dataKey: 'userViewMatrix',
            maxProducts: 4,
            cateryNameStyle: true,
            content_BackGroundColor: '195, 100%, 30%',
        },
        meta: { get title() { return __elakayText("t010", "Views Matrix"); }, color: '#FF6B6B' },
    },

    followShopUserGrid: {
        id: 'followShopUserGrid',
        component: 'MatrixCardsGrid',
        facetKey: 'followShopUser',
        dataKey: 'followShopUserMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG
        requiresPagination: true,
        defaultProps: {
            facet: 'followShopUser',
            // response key from /productsF differs from `facet` here —
            // see historySearchMatrixGrid's comment above.
            dataKey: 'followShopUserMatrix',
            maxProducts: 4,
            cateryNameStyle: true,
            content_BackGroundColor: '195, 100%, 30%',
        },
        // data is unused (selfLoading) — this adapterProps exists only to
        // hand the card's unfollow icon a working loadProductsByArrival
        // (needs context.reloadFacets, only available here).
        adapterProps: ({ sectionProps, context }) => {
            const { reloadFacets } = context;
            const loadProductsByArrival = (_filters, sectionIds) => {
                if (!reloadFacets) return Promise.resolve();
                const ids = Array.isArray(sectionIds)
                    ? sectionIds
                    : sectionIds
                        ? [sectionIds]
                        : ['followMe', 'followShopUser'];
                return reloadFacets(ids);
            };
            return { loadProductsByArrival };
        },
        meta: { get title() { return __elakayText("t012", "Followed Shops"); }, color: '#7B68EE' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CATEGORIES MATRIX
    // ═══════════════════════════════════════════════════════════════════════

    shopCategories: {
        id: 'shopCategories',
        component: 'SwiperCategoriesMatrix',
        facetKey: 'shop_Products_Categories',
        dataKey: 'shopCategories',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: {
            inView: true,
            titleType: 'dynamic',
            titlePrefix: 'categoriesFor',
            titleMiddle: 'Lakay',
        },
        adapterProps: ({ data, sectionProps, context }) => {
            let title = sectionProps.title;
            if (sectionProps.titleType === 'dynamic') {
                const prefix = context.infoSource?.language?.(context.infoSource, 'label', sectionProps.titlePrefix)
                    || __elakayText("t066", "Categories for");
                title = formatLakayShopTitle(data?.shopName, `${prefix} ${sectionProps.titleMiddle || ''}`);
            } else if (sectionProps.titleType === 'lakayShop') {
                title = __elakayText("t067", "Lakay {value0} Categories", {
                    value0: separateLakayWord(data?.shopName),
                });
            } else if (sectionProps.titleType === 'lakayShopUppercase') {
                title = __elakayText("t067", "Lakay {value0} Categories", {
                    value0: separateLakayWord(data?.shopName).toUpperCase(),
                });
            }
            return {
                nbrArr: sectionProps.nbrArr || context.nbrArr?.current || context.nbrArr || 3,
                categoriesMatrix: data,
                title,
            };
        },
        meta: { get title() { return __elakayText("t013", "Shop Categories"); }, color: '#2E8B57' },
    },

    shop_Products_Categories: {
        id: 'shopCategories',
        component: 'SwiperCategoriesMatrix',
        facetKey: 'shop_Products_Categories',
        dataKey: 'shopCategories',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: {
            inView: true,
            titleType: 'dynamic',
            titlePrefix: 'categoriesFor',
            titleMiddle: 'Lakay',
        },
        adapterProps: ({ data, sectionProps, context }) => {
            let title = sectionProps.title;
            if (sectionProps.titleType === 'dynamic') {
                const prefix = context.infoSource?.language?.(context.infoSource, 'label', sectionProps.titlePrefix)
                    || __elakayText("t066", "Categories for");
                title = formatLakayShopTitle(data?.shopName, `${prefix} ${sectionProps.titleMiddle || ''}`);
            } else if (sectionProps.titleType === 'lakayShop') {
                title = __elakayText("t067", "Lakay {value0} Categories", {
                    value0: separateLakayWord(data?.shopName),
                });
            } else if (sectionProps.titleType === 'lakayShopUppercase') {
                title = __elakayText("t067", "Lakay {value0} Categories", {
                    value0: separateLakayWord(data?.shopName).toUpperCase(),
                });
            }
            return {
                nbrArr: sectionProps.nbrArr || context.nbrArr?.current || context.nbrArr || 3,
                categoriesMatrix: data,
                title,
            };
        },
        meta: { get title() { return __elakayText("t013", "Shop Categories"); }, color: '#2E8B57' },
    },

    productCategoryMatrix: {
        id: 'productCategoryMatrix',
        component: 'SwiperCategoriesMatrix',
        facetKey: 'productCategory',
        dataKey: 'productCategory',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: { prodCategory: true },
        adapterProps: ({ data, sectionProps, context }) => ({
            nbrArr: sectionProps.nbrArr || context.nbrArr?.current || context.nbrArr || 3,
            categoriesMatrix: data,
        }),
        meta: { get title() { return __elakayText("t014", "Product Categories"); }, color: '#6B7280' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PUB CATEGORY
    // ═══════════════════════════════════════════════════════════════════════

    pubCategoryProd: {
        id: 'pubCategoryProd',
        component: 'SwiperPubCategoryProdMatrix',
        facetKey: 'pubCategoryProd',
        dataKey: 'lakayPubCatProd',
        loaderType: LOADER_TYPES.LAKAY_USER,
        defaultProps: { Follow: true },
        adapterProps: ({ data }) => ({
            pubCategoryProdMatrix: data,
        }),
        meta: { get title() { return __elakayText("t015", "Ads"); }, color: '#FF4500' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // FOLLOW PAGE
    // ═══════════════════════════════════════════════════════════════════════

    followMe: {
        id: 'followMe',
        component: 'FollowPage',
        facetKey: 'followMe',
        dataKey: 'followMeMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        dataTransform: (data) => data?.[0],
        defaultProps: {},
         adapterProps: ({ data, sectionProps, context }) => {
        const { reloadFacets } = context;

        // ✅ Retourne la Promise de reloadFacets
        // → permet au composant appelant de .then() après le chargement
        const loadProductsByArrival = (_filters, sectionIds) => {
            if (!reloadFacets) return Promise.resolve();
            const ids = Array.isArray(sectionIds)
                ? sectionIds
                : sectionIds
                    ? [sectionIds]
                    : ['followMe', 'followShopUser'];
            return reloadFacets(ids); // ← return la Promise
        };

        return {
            followMeMatrix: data,
            loadProductsByArrival,
        };
    },
        meta: { get title() { return __elakayText("t016", "Follow Suggestions"); }, color: '#32CD32' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRODUCT GRID — facetKey __PRODUCTS_BASE__ → produits paginés de base
    // ═══════════════════════════════════════════════════════════════════════
    productGrid: {
        id: 'productGrid',
        component: 'ProductGridPage',
        facetKey: '__PRODUCTS_BASE__',
        dataKey: 'products',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG: charge ses propres données
        requiresPagination: true,
        meta: { get title() { return __elakayText("t077", "Product Grid"); }, color: '#4A5568' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CATEGORY PRODUCTS LOOP
    // ═══════════════════════════════════════════════════════════════════════

    categoryProductsLoop: {
        id: 'categoryProductsLoop',
        component: 'CategoryProductsLoop',
        facetKey: 'productCategory',
        dataKey: 'productCategory',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {},
        isLoop: true,
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            laguangeElakay: context.laguangeElakay,
            homeStyles: context.homeStyles,
        }),
        meta: { get title() { return __elakayText("t017", "Products by Category"); }, color: '#6B7280' },
    },

    productCategory: {
        id: 'productCategory',
        component: 'CategoryProductsLoop',
        facetKey: 'productCategory',
        dataKey: 'productCategory',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {},
        isLoop: true,
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            laguangeElakay: context.laguangeElakay,
            homeStyles: context.homeStyles,
        }),
        meta: { get title() { return __elakayText("t017", "Products by Category"); }, color: '#6B7280' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // USER PROFILE SECTIONS
    // ═══════════════════════════════════════════════════════════════════════

    userInfoCard: {
        id: 'userInfoCard',
        component: 'UserInfoCard',
        facetKey: 'userInfoCard',
        dataKey: 'userInfo',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            premiumStyles: sectionProps.premiumStyles,
        }),
        meta: { get title() { return __elakayText("t018", "User Info"); }, color: '#007399' },
    },

    userButtons: {
        id: 'userButtons',
        component: 'UserButtonsGroup',
        facetKey: 'userButtons',
        dataKey: 'userInfo',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ sectionProps }) => ({
            handePage: sectionProps.handePage,
        }),
        meta: { get title() { return __elakayText("t019", "Boutons User"); }, color: '#00a4d8' },
    },

    userModal: {
        id: 'userModal',
        component: 'UserModal',
        facetKey: 'userModal',
        dataKey: 'modalState',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            handePage: sectionProps.handePage,
        }),
        meta: { get title() { return __elakayText("t020", "Modal User"); }, color: '#38bdf8' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CART SECTIONS
    // ═══════════════════════════════════════════════════════════════════════

    cartHeader: {
        id: 'cartHeader',
        component: 'CartHeader',
        facetKey: 'cartHeader',
        dataKey: 'cartItems',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t021", "En-tête Panier"); }, color: '#007399' },
    },

    cartItemsList: {
        id: 'cartItemsList',
        component: 'CartItemsList',
        facetKey: 'cartItemsList',
        dataKey: 'cartItems',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            laguangeElakay: context.laguangeElakay,
            Saddress: sectionProps.Saddress,
            SetShipAmount: sectionProps.SetShipAmount,
            cartItems: sectionProps.cartItems,
        }),
        meta: { get title() { return __elakayText("t022", "Liste Articles"); }, color: '#00a4d8' },
    },

    cartCheckout: {
        id: 'cartCheckout',
        component: 'CartCheckout',
        facetKey: 'cartCheckout',
        dataKey: 'cartItems',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            laguangeElakay: context.laguangeElakay,
            Saddress: sectionProps.Saddress,
            setSAddressFinal: sectionProps.setSAddressFinal,
        }),
        meta: { get title() { return __elakayText("t023", "Checkout"); }, color: '#38bdf8' },
    },

    cartEmpty: {
        id: 'cartEmpty',
        component: 'CartEmpty',
        facetKey: 'cartEmpty',
        dataKey: 'cartItems',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        dataCondition: (data) => !data || data.length === 0,
        meta: { get title() { return __elakayText("t024", "Panier Vide"); }, color: '#64748b' },
    },

    cartFullLayout: {
        id: 'cartFullLayout',
        component: 'CartFullLayout',
        facetKey: 'cartFullLayout',
        dataKey: 'cartItems',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        selfLoading: true,    // ← TAG: charge ses propres données
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ORDERS SECTIONS
    // ═══════════════════════════════════════════════════════════════════════

    ordersHeader: {
        id: 'ordersHeader',
        component: 'OrdersHeader',
        facetKey: 'ordersData',
        dataKey: 'orders',
        loaderType: LOADER_TYPES.ORDERS,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            skip: sectionProps.skip,
            limit: sectionProps.limit,
            sizeOrders: sectionProps.sizeOrders,
            setShowFilterOrder: sectionProps.setShowFilterOrder,
        }),
        meta: { get title() { return __elakayText("t025", "En-tête Commandes"); }, color: '#007399' },
    },

    ordersList: {
        id: 'ordersList',
        component: 'OrdersList',
        facetKey: 'ordersData',
        dataKey: 'orders',
        loaderType: LOADER_TYPES.ORDERS,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            loadingTrakingOder: sectionProps.loadingTrakingOder,
            setTrackingOrder: sectionProps.setTrackingOrder,
            setShowTrakingOrder: sectionProps.setShowTrakingOrder,
            bntOderView: sectionProps.bntOderView,
        }),
        meta: { get title() { return __elakayText("t026", "Liste Commandes"); }, color: '#00a4d8' },
    },

    ordersEmpty: {
        id: 'ordersEmpty',
        component: 'OrdersEmpty',
        facetKey: 'ordersData',
        dataKey: 'orders',
        loaderType: LOADER_TYPES.ORDERS,
        defaultProps: {},
        dataCondition: (data) => !data || data.length === 0,
        adapterProps: ({ sectionProps }) => ({
            setShowFilterOrder: sectionProps.setShowFilterOrder,
        }),
        meta: { get title() { return __elakayText("t027", "Pas de Commandes"); }, color: '#64748b' },
    },

    ordersFullLayout: {
        id: 'ordersFullLayout',
        component: 'OrdersFullLayout',
        facetKey: 'ordersData',
        dataKey: 'orders',
        loaderType: LOADER_TYPES.ORDERS,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            skip: sectionProps.skip,
            limit: sectionProps.limit,
            sizeOrders: sectionProps.sizeOrders,
            setShowFilterOrder: sectionProps.setShowFilterOrder,
            loadingTrakingOder: sectionProps.loadingTrakingOder,
            setTrackingOrder: sectionProps.setTrackingOrder,
            setShowTrakingOrder: sectionProps.setShowTrakingOrder,
            bntOderView: sectionProps.bntOderView,
            loadMore: sectionProps.loadMore,
            loadMore0: sectionProps.loadMore0,
        }),
        meta: { get title() { return __elakayText("t028", "Layout Commandes"); }, color: '#007399' },
    },

    ordersModals: {
        id: 'ordersModals',
        component: 'OrdersModals',
        facetKey: 'ordersModals',
        dataKey: 'modalsState',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            token: sectionProps.token,
            filtOrders: sectionProps.filtOrders,
            limit: sectionProps.limit,
            setLimit: sectionProps.setLimit,
            setFiltOrders: sectionProps.setFiltOrders,
            trackingOrder: sectionProps.trackingOrder,
            getOrderID: sectionProps.getOrderID,
            pageImgSms: sectionProps.pageImgSms,
            setPageImgSms: sectionProps.setPageImgSms,
            graphicNote: sectionProps.graphicNote,
            setGraphicNote: sectionProps.setGraphicNote,
            graphicNoteBs: sectionProps.graphicNoteBs,
            setGraphicNoteBs: sectionProps.setGraphicNoteBs,
            graphicImgBs: sectionProps.graphicImgBs,
            setGraphicImgBs: sectionProps.setGraphicImgBs,
            setShowOrderNote: sectionProps.setShowOrderNote,
        }),
        meta: { get title() { return __elakayText("t029", "Modals Commandes"); }, color: '#38bdf8' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ORDERS-RELATED PRODUCT SECTIONS
    // ═══════════════════════════════════════════════════════════════════════

    productsByCategory: {
        id: 'productsByCategory',
        component: 'ProductsByCategory',
        facetKey: 'productCategory',
        dataKey: 'categoryID',
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {},
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t017", "Products by Category"); }, color: '#6B7280' },
    },

    lakayUserSection: {
        id: 'lakayUserSection',
        component: 'LakayUserSection',
        facetKey: 'shop_Products_Categories',
        dataKey: 'infoOwner',
        loaderType: LOADER_TYPES.LAKAY_USER,
        defaultProps: {},
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t030", "Lakay User"); }, color: '#2E8B57' },
    },

    newProductSection: {
        id: 'newProductSection',
        component: 'NewProductSection',
        facetKey: 'newProducts',
        dataKey: null,
        loaderType: LOADER_TYPES.PRODUCTS,
        defaultProps: {},
        meta: { get title() { return __elakayText("t031", "Nouveau Product"); }, color: '#FF69B4' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRODUCT PREVIEW SECTIONS
    // ═══════════════════════════════════════════════════════════════════════

    productUnavailable: {
        id: 'productUnavailable',
        component: 'ProductUnavailable',
        facetKey: 'productPreviewData',
        dataKey: 'itemUnAvailable',
        loaderType: LOADER_TYPES.PRODUCT_PREVIEW,
        defaultProps: {},
        dataCondition: (data) => data === true,
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t032", "Product Indisponible"); }, color: '#DC143C' },
    },

    productViewMain: {
        id: 'productViewMain',
        component: 'ProductViewMain',
        facetKey: 'productPreviewData',
        dataKey: 'product',
        loaderType: LOADER_TYPES.PRODUCT_PREVIEW,
        defaultProps: {},
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            laguangeElakay: context.laguangeElakay,
            images: sectionProps.images,
            oneFilterPro: sectionProps.oneFilterPro,
            quantity: sectionProps.quantity,
            count: sectionProps.count,
            setcount: sectionProps.setcount,
            priceProd: sectionProps.priceProd,
            etoiteProduct: sectionProps.etoiteProduct,
            inputCart: sectionProps.inputCart,
            addToCart: sectionProps.addToCart,
            Saddress: sectionProps.Saddress,
            setSAddressFinal: sectionProps.setSAddressFinal,
            addressShop: sectionProps.addressShop,
            cartItemsPRoNew: sectionProps.cartItemsPRoNew,
            parcel: sectionProps.parcel,
            galeries: sectionProps.galeries,
            unSeFil: sectionProps.unSeFil,
            setUnSefil: sectionProps.setUnSefil,
            setImages: sectionProps.setImages,
            setOneFilterPro: sectionProps.setOneFilterPro,
            setALLFilterPro: sectionProps.setALLFilterPro,
            restAllFilterPro: sectionProps.restAllFilterPro,
            allFilterPro: sectionProps.allFilterPro,
            getQueryFil: sectionProps.getQueryFil,
            setNewAP: sectionProps.setNewAP,
            setQuantity: sectionProps.setQuantity,
        }),
        meta: { get title() { return __elakayText("t033", "Vue Product"); }, color: '#007399' },
    },

    productDetailsTabs: {
        id: 'productDetailsTabs',
        component: 'ProductDetailsTabs',
        facetKey: 'productPreviewData',
        dataKey: 'product',
        loaderType: LOADER_TYPES.PRODUCT_PREVIEW,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            oneFilterPro: sectionProps.oneFilterPro,
        }),
        meta: { get title() { return __elakayText("t034", "Détails Product"); }, color: '#00a4d8' },
    },

    productComments: {
        id: 'productComments',
        component: 'ProductComments',
        facetKey: 'productCommentsData',
        dataKey: 'commentsData',
        loaderType: LOADER_TYPES.COMMENTS,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            productId: sectionProps.productId,
            token: sectionProps.token,
            skip: sectionProps.skip,
            limit: sectionProps.limit,
            loadProductsView: sectionProps.loadProductsView,
            ButtonAddComments: sectionProps.ButtonAddComments,
            showComments: sectionProps.showComments,
            setShowComments: sectionProps.setShowComments,
            commentProduct: sectionProps.commentProduct,
        }),
        meta: { get title() { return __elakayText("t035", "Commentaires"); }, color: '#38bdf8' },
    },

    productImagesNote: {
        id: 'productImagesNote',
        component: 'ProductImagesNote',
        facetKey: 'productPreviewData',
        dataKey: 'product',
        loaderType: LOADER_TYPES.PRODUCT_PREVIEW,
        defaultProps: {},
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t036", "Images Note"); }, color: '#6B7280' },
    },

    productCommentsModal: {
        id: 'productCommentsModal',
        component: 'ProductCommentsModal',
        facetKey: 'productCommentsData',
        dataKey: 'commentsModalState',
        loaderType: LOADER_TYPES.COMMENTS,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            productId: sectionProps.productId,
            loadProductsView: sectionProps.loadProductsView,
            commentProduct: sectionProps.commentProduct,
            ButtonAddComments: sectionProps.ButtonAddComments,
        }),
        meta: { get title() { return __elakayText("t037", "Modal Commentaires"); }, color: '#FF6B6B' },
    },

    productFullView: {
        id: 'productFullView',
        component: 'ProductFullView',
        facetKey: 'productPreviewData',
        dataKey: 'product',
        loaderType: LOADER_TYPES.PRODUCT_PREVIEW,
        defaultProps: {},
        adapterProps: ({ data, sectionProps, context }) => ({
            data,
            laguangeElakay: context.laguangeElakay,
            images: sectionProps.images,
            oneFilterPro: sectionProps.oneFilterPro,
            quantity: sectionProps.quantity,
            count: sectionProps.count,
            setcount: sectionProps.setcount,
            priceProd: sectionProps.priceProd,
            inputCart: sectionProps.inputCart,
            addToCart: sectionProps.addToCart,
            Saddress: sectionProps.Saddress,
            setSAddressFinal: sectionProps.setSAddressFinal,
            addressShop: sectionProps.addressShop,
            cartItemsPRoNew: sectionProps.cartItemsPRoNew,
            parcel: sectionProps.parcel,
            galeries: sectionProps.galeries,
            unSeFil: sectionProps.unSeFil,
            setUnSefil: sectionProps.setUnSefil,
            setImages: sectionProps.setImages,
            setOneFilterPro: sectionProps.setOneFilterPro,
            setALLFilterPro: sectionProps.setALLFilterPro,
            restAllFilterPro: sectionProps.restAllFilterPro,
            allFilterPro: sectionProps.allFilterPro,
            getQueryFil: sectionProps.getQueryFil,
            setNewAP: sectionProps.setNewAP,
            setQuantity: sectionProps.setQuantity,
        }),
        meta: { get title() { return __elakayText("t038", "Vue Complète Product"); }, color: '#007399' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SHOP USER VIEW SECTIONS
    // ═══════════════════════════════════════════════════════════════════════

    shopHeader: {
        id: 'shopHeader',
        component: 'ShopHeader',
        facetKey: 'shop_Owner',
        dataKey: 'shopOwner',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: {
            outOffPage: true,
            showFilter: true,
            myShop: false,
        },
        adapterProps: ({ data, sectionProps }) => ({
            data,
            userID: sectionProps.userID,
            setFilterShop: sectionProps.setFilterShop,
            onFilterShop: sectionProps.onFilterShop,
            loadindSearCh: sectionProps.loadindSearCh,
            search: sectionProps.search,
            outOffPage: sectionProps.outOffPage,
            showFilter: sectionProps.showFilter,
            myShop: sectionProps.myShop,
            setEditStore: sectionProps.setEditStore,
            editStore: sectionProps.editStore,
        }),
        meta: { get title() { return __elakayText("t039", "En-tête Shop"); }, color: '#007399' },
    },

    shopSliderPub: {
        id: 'shopSliderPub',
        component: 'ShopSliderPub',
        facetKey: 'shopLakayUser',
        dataKey: 'shopPub',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: { shop: true },
        adapterProps: ({ data, sectionProps }) => ({
            data,
            shopOwner: sectionProps.shopOwner,
        }),
        meta: { get title() { return __elakayText("t040", "Slider Pub Shop"); }, color: '#00a4d8' },
    },

    shopCategoriesMatrix: {
        id: 'shopCategoriesMatrix',
        component: 'SwiperCategoriesMatrix',
        facetKey: 'shop_Products_Categories',
        dataKey: 'shopCategories',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: {
            inView: true,
            backgroundColor: '#cce3ea',
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            nbrArr: sectionProps.nbrArr || context.nbrArr?.current || context.nbrArr || 3,
            categoriesMatrix: data,
            loadProductsByArrival: sectionProps.loadProductsByArrival_,
            setSearch: sectionProps.setSearch,
            backgroundColor: sectionProps.backgroundColor || '#cce3ea',
        }),
        meta: { get title() { return __elakayText("t041", "Categories Shop"); }, color: '#2E8B57' },
    },

    shopPubCategoryMatrix: {
        id: 'shopPubCategoryMatrix',
        component: 'SwiperPubCategoryProdMatrix',
        facetKey: 'pubCategoryProd',
        dataKey: 'shopPubCategory',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: {
            Follow: true,
            inView: true,
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            pubCategoryProdMatrix: data,
            Follow: sectionProps.Follow !== false,
            booFollow: sectionProps.booFollow,
            unFollow: sectionProps.unFollow,
            unFollowShop: sectionProps.unFollowShop,
            findToFollowU: sectionProps.findToFollowU,
            setSearch: sectionProps.setSearch,
            loadProductsByArrival: sectionProps.loadProductsByArrival_,
            ci: sectionProps.ci,
            activeFollowBtn: sectionProps.activeFollowBtn,
            nbrArr: sectionProps.nbrArr || context.nbrArr?.current || context.nbrArr || 3,
            userID: sectionProps.userID,
        }),
        meta: { get title() { return __elakayText("t042", "Pub Category Shop"); }, color: '#FF4500' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SHOP PRODUCT GRID
    // ═══════════════════════════════════════════════════════════════════════
    //
    // ✅ v5.3: adapterProps utilise buildGridAdapterProps
    // → loadMore/loadMore0 transmettent { sectionId: 'shopProductGrid', facetKey: 'shopLakayUser' }
    // → reloadFacets(['shopProductGrid']) recharge UNIQUEMENT shopProducts

    shopProductGrid: {
        id: 'shopProductGrid',
        component: 'ProductGridShop',
        facetKey: 'shopLakayUser',
        dataKey: 'shopProducts',
        loaderType: LOADER_TYPES.SHOP_USER,
        defaultProps: {
            gridAnimated: false,
            animateCards: false,
            resultAnimated: false,
        },
        requiresPagination: true,
        // adapterProps: ({ data, sectionProps, context }) =>
        //     buildGridAdapterProps({ data, sectionProps, context, defaultLimit: 12 }),
        meta: { get title() { return __elakayText("t043", "Grille Products Shop"); }, color: '#4A5568' },
    },

    shopModals: {
        id: 'shopModals',
        component: 'ShopModals',
        facetKey: 'shopModals',
        dataKey: 'shopModalsState',
        loaderType: LOADER_TYPES.NONE,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            data,
            token: sectionProps.token,
            shopOwner: sectionProps.shopOwner,
            myShop: sectionProps.myShop,
        }),
        meta: { get title() { return __elakayText("t044", "Modals Shop"); }, color: '#38bdf8' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SECTIONS UNIVERSELLES
    // ═══════════════════════════════════════════════════════════════════════

    userViewedDirect: {
        id: 'userViewedDirect',
        component: 'Slider',
        facetKey: 'userViewedDirect',
        dataKey: 'userViewedProduct',
        loaderType: LOADER_TYPES.USER_VIEWED,
        defaultProps: {
            nameSliderKey: 'yourViewed',
            linkSlider: '/user-viewed',
            CataID: true,
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            ProductSwiper: data,
            User: context.infoSource?.readUser,
            nameSlider: context.infoSource?.language?.(context.infoSource, 'homePage', 'yourViewed') || 'Recently Viewed',
            linkSlider: sectionProps.linkSlider || '/user-viewed',
            CataID: true,
            myStyles: context.homeStyles,
        }),
        meta: { get title() { return __elakayText("t045", "Products vus (direct)"); }, color: '#F59E0B' },
    },

    categoryMetadata: {
        id: 'categoryMetadata',
        component: 'CategoryMetadata',
        facetKey: 'categoryMetadata',
        dataKey: 'categoriesProd',
        loaderType: LOADER_TYPES.CATEGORIES,
        defaultProps: {},
        adapterProps: ({ data }) => ({
            categoriesProd: data,
            PEPCData: data?.cartegory?.PEPCData || data?.PEPCData,
            categoryPub: data?.pub,
            categoryCartegory: data?.cartegory,
        }),
        meta: { get title() { return __elakayText("t046", "Metadata Category"); }, color: '#4A90D9' },
    },

    productPreviewData: {
        id: 'productPreviewData',
        component: 'ProductPreviewData',
        facetKey: 'productPreviewData',
        dataKey: 'product',
        loaderType: LOADER_TYPES.PRODUCT_PREVIEW,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            product: data,
            galeries: sectionProps.galeries,
            filterProduct: sectionProps.filterProduct,
            productFilters: sectionProps.productFilters,
            shopAddress: sectionProps.shopAddress,
        }),
        meta: { get title() { return __elakayText("t047", "Données Product Preview"); }, color: '#007399' },
    },

    productCommentsData: {
        id: 'productCommentsData',
        component: 'ProductComments',
        facetKey: 'productCommentsData',
        dataKey: 'commentsPro',
        loaderType: LOADER_TYPES.COMMENTS,
        defaultProps: {},
        adapterProps: ({ data, sectionProps }) => ({
            comments: data,
            commentsCount: sectionProps.commentsCount,
            userVote: sectionProps.userVote,
            productId: sectionProps.productId,
        }),
        meta: { get title() { return __elakayText("t048", "Données Commentaires"); }, color: '#38bdf8' },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PRODUCT GRID VARIANTS — *Grid paginés indépendants
    // ═══════════════════════════════════════════════════════════════════════
    //
    // ✅ v5.3: tous utilisent buildGridAdapterProps
    // INDEPENDENT_SECTION_IDS (DynamicPageRenderer) garantit que seules
    // ces sections reçoivent paginationProps avec { sectionId, facetKey, dataKey }.
    // loadMore/loadMore0 rechargent UNIQUEMENT leur propre facet.

    userViewGrid: {
        id: 'userViewGrid',
        component: 'ProductGridPage',
        facetKey: 'userView',
        dataKey: 'userViewedProduct',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG
        requiresPagination: true,
        meta: { get title() { return __elakayText("t078", "Viewed Products Grid"); }, color: '#20B2AA' },
    },

    dealTodaysGrid: {
        id: 'dealTodaysGrid',
        component: 'ProductGridPage',
        facetKey: 'dealTodays',
        dataKey: 'dealToDayProducts',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG
        requiresPagination: true,
        meta: { get title() { return __elakayText("t079", "Today Deals Grid"); }, color: '#DC143C' },
    },

    bestSellersGrid: {
        id: 'bestSellersGrid',
        component: 'ProductGridPage',
        facetKey: 'bestSellers',
        dataKey: 'productsBySell',
        loaderType: LOADER_TYPES.PRODUCTS,
        selfLoading: true,          // ← TAG
        requiresPagination: true,
        meta: { get title() { return __elakayText("t080", "Best Sellers Grid"); }, color: '#FFD700' },
    },

    productSearch: {
        id: 'productSearch',
        component: 'ProductGridSearch',
        facetKey: 'productSearch',
        dataKey: 'products',
        loaderType: LOADER_TYPES.SEARCH,
        selfLoading: true,
        requiresPagination: true,
        defaultProps: {
            skip: 0,
            limit: 12,
            resultStyle: 'minimal',
            gridStyle: 'minimal',
            resultAnimated: false,
            animateCards: false,
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            // NOTE: stays uncontrolled (ProductGridSearch keeps its own
            // self-managed skip/loadMore state) — this page never wires a
            // paginationProps.loadMore, so switching to controlled mode
            // would silently break the "load more" button. The duplicate
            // *initial* fetch is avoided instead via `initialData` (see
            // DynamicPageRenderer.jsx), which only seeds the first render.
            controlled: false,
            productsByArrival: data,
            size: context.pageData?.size ?? 0,
            siseProduct: context.pageData?.siseProduct ?? 0,
            skip: context.paginationProps?.skip ?? 0,
            limit: context.paginationProps?.limit ?? 12,
            loadMore: context.paginationProps?.loadMore,
            loadMore0: context.paginationProps?.loadMore0,
            search: sectionProps.search,
            infoSource: context.infoSource,
            laguangeElakay: context.laguangeElakay,
            ...sectionProps,
        }),
        meta: { get title() { return __elakayText("t049", "Résultats Search"); }, color: '#FFA500' },
    },

    productSearchGrid: {
        id: 'productSearchGrid',
        component: 'ProductGridSearch',
        facetKey: 'productSearch',
        dataKey: 'products',
        loaderType: LOADER_TYPES.SEARCH,
        selfLoading: true,
        requiresPagination: true,
        defaultProps: {
            skip: 0,
            limit: 12,
            resultStyle: 'minimal',
            gridStyle: 'minimal',
            resultAnimated: false,
            animateCards: false,
        },
        adapterProps: ({ data, sectionProps, context }) => ({
            // NOTE: stays uncontrolled (ProductGridSearch keeps its own
            // self-managed skip/loadMore state) — this page never wires a
            // paginationProps.loadMore, so switching to controlled mode
            // would silently break the "load more" button. The duplicate
            // *initial* fetch is avoided instead via `initialData` (see
            // DynamicPageRenderer.jsx), which only seeds the first render.
            controlled: false,
            productsByArrival: data,
            size: context.pageData?.size ?? 0,
            siseProduct: context.pageData?.siseProduct ?? 0,
            skip: context.paginationProps?.skip ?? 0,
            limit: context.paginationProps?.limit ?? 12,
            loadMore: context.paginationProps?.loadMore,
            loadMore0: context.paginationProps?.loadMore0,
            search: sectionProps.search,
            infoSource: context.infoSource,
            laguangeElakay: context.laguangeElakay,
            ...sectionProps,
        }),
        meta: { get title() { return __elakayText("t049", "Résultats Search"); }, color: '#FFA500' },
    },

    investmentProjects: {
        id: 'investmentProjects',
        component: 'InvestmentHomeSection',
        facetKey: 'investmentProjects',
        dataKey: 'investmentCatalog',
        loaderType: LOADER_TYPES.INVESTMENTS,
        defaultProps: {
            skip: 0,
            limit: 12,
        },
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t070", "Investment Marketplace"); }, color: '#13c784' },
    },

    investmentProject: {
        id: 'investmentProject',
        component: 'InvestmentDetailSection',
        facetKey: 'investmentProject',
        dataKey: 'investmentProject',
        loaderType: LOADER_TYPES.INVESTMENTS,
        defaultProps: {},
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t071", "Investment Detail"); }, color: '#0d1f1b' },
    },

    jobPostings: {
        id: 'jobPostings',
        component: 'JobHomeSection',
        facetKey: 'jobPostings',
        dataKey: 'jobCatalog',
        loaderType: LOADER_TYPES.JOBS,
        defaultProps: {
            skip: 0,
            limit: 12,
        },
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t072", "Elakay Jobs"); }, color: '#f26a4b' },
    },

    jobPosting: {
        id: 'jobPosting',
        component: 'JobDetailSection',
        facetKey: 'jobPosting',
        dataKey: 'jobPosting',
        loaderType: LOADER_TYPES.JOBS,
        defaultProps: {},
        adapterProps: ({ data }) => ({ data }),
        meta: { get title() { return __elakayText("t073", "Job Detail"); }, color: '#17221f' },
    },

    ordersGrid: {
    id: 'ordersGrid',
    component: 'OrdersGridPage',
    facetKey: 'orders',
    dataKey: 'orders',
    loaderType: LOADER_TYPES.ORDERS,
    selfLoading: true,           // ← gère sa propre pagination
    requiresPagination: true,
    defaultProps: {
        skip: 0,
        limit: 12,
        resultStyle: 'minimal',
        gridStyle: 'minimal',
        resultAnimated: false,
        animateCards: false,
        showFilterButton: true,
    },
    // adapterProps: ({ data, sectionProps, context }) => ({
    //     controlled: false,
        // orders: data,
        // sizeOrders: context.pageData?.ordersCount ?? 0,
        // size: context.paginationProps?.size ?? (data?.length ?? 0),
        // skip: context.paginationProps?.skip ?? 0,
        // limit: context.paginationProps?.limit ?? 12,
        // loadMore: context.paginationProps?.loadMore,
        // loadMore0: context.paginationProps?.loadMore0,
        // infoSource: context.infoSource,
        // laguangeElakay: context.laguangeElakay,
    //     ...sectionProps,
    // }),
    meta: { get title() { return __elakayText("t050", "Orders List"); }, color: '#10B981' },
},
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGES CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export const PAGES_CONFIG_ = {

    HOME: {
        id: 'HOME',
        get name() { return __elakayText("t051", "Home"); },
        icon: <FaHome />,
        sections: buildPageSections('HOME', {
            shopCategories: { props: { titleType: 'dynamic', titlePrefix: 'categoriesFor', titleMiddle: 'Lakay' } },
            followShopUser: { props: { maxProducts: 4 } },
        }),
    },

    AAA: {
        id: 'AAA',
        icon: <FaTags />,
        get name() { return __elakayText("t052", "Product Category AAA"); },
        sections: buildPageSections('AAA', {
            followShopUser: { props: { get title() { return __elakayText("t011", "Shops You Follow"); }, maxProducts: 4, type_Animation: null, animation: false } },
            shopCategories: { props: { nbrArr: 4, inView: true, textFoorter: true, titleType: 'lakayShopUppercase' } },
        }),
    },

    AAB: {
        id: 'AAB',
        icon: <FaTags />,
        get name() { return __elakayText("t053", "Product Category AAB"); },
        sections: buildPageSections('AAB', {
            followShopUser: { props: { maxProducts: 4, cateryNameStyle: true } },
        }),
    },

    AAC: {
        id: 'AAC',
        icon: <FaTags />,
        get name() { return __elakayText("t054", "Product Category AAC"); },
        sections: buildPageSections('AAC', {
            productCategoryMatrix: { props: { prodCategory: true } },
            historySearchMatrix: { props: { cateryNameStyle: true } },
            followShopUser: { props: { maxProducts: 4, cateryNameStyle: true } },
        }),
    },

    DEFAULT: {
        id: 'DEFAULT',
        get name() { return __elakayText("t055", "Default Page"); },
        sections: [
            { sectionId: 'productGrid', order: 0 },
            { sectionId: 'followMe',    order: 1 },
            { sectionId: 'dealTodays',  order: 2 },
            { sectionId: 'bestSellers', order: 3 },
        ],
    },

    USER_PROFILE: {
        id: 'USER_PROFILE',
        icon: <FaUser />,
        get name() { return __elakayText("t056", "User Profile"); },
        sections: buildPageSections('USER_PROFILE', {
            userView: { props: { nameSliderKey: 'yourViewed', linkSlider: '/user-viewed', CataID: true } },
        }),
    },

    CART: {
        id: 'CART',
        icon: <FaShoppingCart />,
        get name() { return __elakayText("t057", "Shopping Cart"); },
        sections: buildPageSections('CART'),
    },

    ORDERS: {
        id: 'ORDERS',
        icon: <FaBox />,
        get name() { return __elakayText("t058", "My Orders"); },
        sections: buildPageSections('ORDERS'),
    },

    PRODUCT_PREVIEW: {
        id: 'PRODUCT_PREVIEW',
        icon: <FaEye />,
        get name() { return __elakayText("t059", "Product Preview"); },
        sections: buildPageSections('PRODUCT_PREVIEW'),
    },

    PRODUCT_EDIT: {
        id: 'PRODUCT_EDIT',
        icon: <FaEdit />,
        get name() { return __elakayText("t068", "Product Edit"); },
        sections: buildPageSections('PRODUCT_EDIT'),
    },

    SHOP_USER: {
        id: 'SHOP_USER',
        icon: <FaStore />,
        get name() { return __elakayText("t060", "Shop User View"); },
        sections: buildPageSections('SHOP_USER'),
    },

    newProducts: {
        id: 'newProducts',
        icon: <FaStar />,
        get name() { return __elakayText("t061", "New Products"); },
        sections: buildPageSections('newProducts', {
            productGrid: { props: { dataKey: 'productsByArrival' } },
        }),
    },

    bestSellers: {
        id: 'bestSellers',
        icon: <FaFire />,
        get name() { return __elakayText("t004", "Best Sellers"); },
        sections: buildPageSections('bestSellers', {
            bestSellersGrid: {
                overrides: {
                    component: 'ProductGridPage',
                    facetKey: 'bestSellers',
                    dataKey: 'productsBySell',
                    skip: 0,
                    limit: 12,
                },
            },
            followShopUser: { props: { maxProducts: 4, cateryNameStyle: true } },
        }),
    },

    dealTodays: {
        id: 'dealTodays',
        icon: <FaTag />,
        get name() { return __elakayText("t062", "Deal of the Day"); },
        sections: buildPageSections('dealTodays'),
    },

    productSearch: {
        id: 'productSearch',
        icon: <FaSearch />,
        get name() { return __elakayText("t063", "Search"); },
        sections: buildPageSections('productSearch', {
            followShopUser: { props: { maxProducts: 4, cateryNameStyle: true } },
        }),
    },

    INVESTMENTS_HOME: {
        id: 'INVESTMENTS_HOME',
        icon: <FaLayerGroup />,
        get name() { return __elakayText("t070", "Investment Marketplace"); },
        sections: buildPageSections('INVESTMENTS_HOME'),
    },

    INVESTMENT_DETAIL: {
        id: 'INVESTMENT_DETAIL',
        icon: <FaInfoCircle />,
        get name() { return __elakayText("t071", "Investment Detail"); },
        sections: buildPageSections('INVESTMENT_DETAIL'),
    },

    JOBS_HOME: {
        id: 'JOBS_HOME',
        icon: <FaLayerGroup />,
        get name() { return __elakayText("t072", "Elakay Jobs"); },
        sections: buildPageSections('JOBS_HOME'),
    },

    JOB_DETAIL: {
        id: 'JOB_DETAIL',
        icon: <FaInfoCircle />,
        get name() { return __elakayText("t073", "Job Detail"); },
        sections: buildPageSections('JOB_DETAIL'),
    },

    userView: {
        id:   'userView',
        get name() { return __elakayText("t064", "User Viewed"); },
        sections: buildPageSections('userView'),
    },

    CATEGORIES: {
        id: 'CATEGORIES',
        icon: <FaTags />,
        get name() { return __elakayText("t069", "Categories"); },
        sections: buildPageSections('CATEGORIES'),
    },

    DEFAULT_ALL: {
        id: 'DEFAULT_ALL',
        get name() { return __elakayText("t065", "All Sections"); },
        sections: buildPageSections('DEFAULT_ALL'),
    },

    // Section-level "See More" all-cards pages (2026-08-27).
    searchHistoryAllCards: {
        id: 'searchHistoryAllCards',
        icon: <FaSearch />,
        get name() { return __elakayText("t074", "Search History — All Cards"); },
        sections: buildPageSections('searchHistoryAllCards'),
    },

    viewingActivityAllCards: {
        id: 'viewingActivityAllCards',
        icon: <FaEye />,
        get name() { return __elakayText("t075", "Viewing Activity — All Cards"); },
        sections: buildPageSections('viewingActivityAllCards'),
    },

    shopsYouFollowAllCards: {
        id: 'shopsYouFollowAllCards',
        icon: <FaStore />,
        get name() { return __elakayText("t076", "Shops You Follow — All Cards"); },
        sections: buildPageSections('shopsYouFollowAllCards'),
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// FONCTIONS UTILITAIRES
// ─────────────────────────────────────────────────────────────────────────────

export const getSection = (sectionId) => SECTIONS_LIBRARY[sectionId] || null;

export const getPageConfig = (pageId) => PAGES_CONFIG_[pageId] || PAGES_CONFIG_.DEFAULT;

export const getPageSections = (pageId) => {
    const pageConfig = getPageConfig(pageId);

    return pageConfig.sections
        .map(sectionEntry => {
            const baseSection = getSection(sectionEntry.sectionId);
            if (!baseSection) {
                console.warn(`Section "${sectionEntry.sectionId}" not found in library`);
                return null;
            }

            const overrides  = sectionEntry.overrides || {};
            const entryProps = sectionEntry.props     || {};

            const resolvedDataKey = overrides.dataKey
                ?? entryProps.dataKey
                ?? baseSection.dataKey;

            const finalProps = {
                ...baseSection.defaultProps,
                ...entryProps,
            };

            if (overrides.skip  !== undefined && overrides.skip  !== null) finalProps.skip  = overrides.skip;
            if (overrides.limit !== undefined && overrides.limit !== null) finalProps.limit = overrides.limit;

            return {
                ...baseSection,
                component: overrides.component ?? baseSection.component,
                facetKey:  overrides.facetKey  ?? baseSection.facetKey,
                dataKey:   resolvedDataKey,
                order:     sectionEntry.order,
                enabled:   sectionEntry.enabled !== false,
                props:     finalProps,
            };
        })
        .filter(section => section !== null && section.enabled)
        .sort((a, b) => a.order - b.order);
};

export const getPageFacets = (pageId) => {
    const sections  = getPageSections(pageId);
    const facetKeys = sections.map(s => s.facetKey).filter(Boolean);
    return [...new Set(facetKeys)];
};

export const listAllFacets = () => {
    return [...new Set(
        Object.values(SECTIONS_LIBRARY)
            .map(s => s.facetKey)
            .filter(Boolean)
    )];
};

export const listAllSections = () => {
    return Object.values(SECTIONS_LIBRARY).map(s => ({
        id: s.id,
        component: s.component,
        facetKey: s.facetKey,
        loaderType: s.loaderType,
        meta: s.meta,
    }));
};

export const getFacetToDataKeyMap = () => {
    const map = {};
    Object.values(SECTIONS_LIBRARY).forEach(section => {
        if (section.facetKey && section.dataKey) {
            map[section.facetKey] = section.dataKey;
        }
    });
    return map;
};

export const groupFacetsByLoader = (facetKeys, externalRegistry = null) => {
    const groups     = {};
    const foundFacets = new Set();

    Object.values(SECTIONS_LIBRARY).forEach(section => {
        const fk = section.facetKey;
        if (!fk || fk === LOADER_TYPES.NONE) return;
        if (!section.loaderType || section.loaderType === LOADER_TYPES.NONE) return;

        if (fk === PRODUCTS_BASE_FACET && facetKeys.includes(section.id)) {
            if (!groups[section.loaderType]) groups[section.loaderType] = [];
            if (!groups[section.loaderType].includes(PRODUCTS_BASE_FACET)) {
                groups[section.loaderType].push(PRODUCTS_BASE_FACET);
            }
            foundFacets.add(section.id);
            return;
        }

        if (typeof fk === 'string' && facetKeys.includes(fk)) {
            if (!groups[section.loaderType]) groups[section.loaderType] = [];
            if (!groups[section.loaderType].includes(fk)) {
                groups[section.loaderType].push(fk);
            }
            foundFacets.add(fk);
        }
    });

    if (externalRegistry) {
        facetKeys.forEach(fk => {
            if (foundFacets.has(fk)) return;
            const reg = externalRegistry[fk];
            if (reg?.loaderType && reg.loaderType !== LOADER_TYPES.NONE) {
                if (!groups[reg.loaderType]) groups[reg.loaderType] = [];
                if (!groups[reg.loaderType].includes(reg.facetKey || fk)) {
                    groups[reg.loaderType].push(reg.facetKey || fk);
                }
            }
        });
    }

    return groups;
};

// export const shouldRenderSection = (sectionConfig, pageData) => {
//     if (!sectionConfig) return false;
//     if (!sectionConfig.dataKey) return true;

//     const data = pageData?.[sectionConfig.dataKey];

//     if (sectionConfig.dataCondition) {
//         return sectionConfig.dataCondition(data);
//     }

//     if (data === undefined || data === null) return false;
//     if (Array.isArray(data) && data.length === 0) return false;

//     return true;
// };

export const shouldRenderSection = (sectionConfig, pageData) => {
    if (!sectionConfig) return false;

    // ✅ Sections selfLoading → toujours affichées si le facet est présent
    // Le composant charge ses propres données — pageData non requis
    if (sectionConfig.selfLoading === true) return true;

    if (!sectionConfig.dataKey) return true;

    const data = pageData?.[sectionConfig.dataKey];

    if (sectionConfig.dataCondition) {
        return sectionConfig.dataCondition(data);
    }
  if (['orders'].includes(sectionConfig.dataKey)) return true;
    if (data === undefined || data === null) return false;
    if (Array.isArray(data) && data.length === 0) return false;

    return true;
};

export const transformSectionData = (sectionConfig, pageData) => {
    if (!sectionConfig.dataKey) return null;
    const rawData = pageData?.[sectionConfig.dataKey];
    if (sectionConfig.dataTransform) return sectionConfig.dataTransform(rawData);
    return rawData;
};

export const listAvailableSections  = () => Object.keys(SECTIONS_LIBRARY);
export const listConfiguredPages    = () => Object.keys(PAGES_CONFIG_);

export const registerSection = (sectionId, sectionConfig) => {
    SECTIONS_LIBRARY[sectionId] = { id: sectionId, ...sectionConfig };
};

export const registerPage = (pageId, pageConfig) => {
    PAGES_CONFIG_[pageId] = { id: pageId, ...pageConfig };
};

export const addSectionToPage = (pageId, sectionEntry) => {
    const pageConfig = PAGES_CONFIG_[pageId];
    if (!pageConfig) { console.error(`Page "${pageId}" not found`); return false; }
    pageConfig.sections.push(sectionEntry);
    pageConfig.sections.sort((a, b) => a.order - b.order);
    return true;
};

export const removeSectionFromPage = (pageId, sectionId) => {
    const pageConfig = PAGES_CONFIG_[pageId];
    if (!pageConfig) return false;
    pageConfig.sections = pageConfig.sections.filter(s => s.sectionId !== sectionId);
    return true;
};

export const updateSectionOrder = (pageId, sectionId, newOrder) => {
    const pageConfig = PAGES_CONFIG_[pageId];
    if (!pageConfig) return false;
    const section = pageConfig.sections.find(s => s.sectionId === sectionId);
    if (section) {
        section.order = newOrder;
        pageConfig.sections.sort((a, b) => a.order - b.order);
        return true;
    }
    return false;
};

export default PAGES_CONFIG_;
